import { memo, useEffect, useRef } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";

interface TemplateContentProps {
  content: string;
  className?: string;
  onPlaceholderChange?: (placeholder: string, value: string) => void;
  placeholderValues?: Record<string, string>;
  onSave?: () => void;
  displayAsForm?: boolean;
}

export const TemplateContent = memo(({ 
  content,
  className = "",
  onPlaceholderChange,
  placeholderValues = {},
  onSave,
  displayAsForm = false
}: TemplateContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showEmptyFieldsDialog, setShowEmptyFieldsDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);

  // Function to sanitize HTML content
  const sanitizeHtml = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Remove all style tags (but keep external CSS classes)
    doc.querySelectorAll('style').forEach(style => style.remove());

    // Remove inline style attributes but preserve CSS classes
    doc.querySelectorAll('*').forEach(element => {
      element.removeAttribute('style');
      // Keep all template-related CSS classes and placeholder classes
      const classAttr = element.getAttribute('class');
      if (classAttr) {
        // Keep template classes, placeholder classes, and other important classes
        const classes = classAttr.split(' ').filter(cls => 
          cls.startsWith('template-') || 
          cls.startsWith('placeholder') || 
          ['template-content', 'placeholder-input'].includes(cls)
        );
        if (classes.length > 0) {
          element.setAttribute('class', classes.join(' '));
        } else {
          element.removeAttribute('class');
        }
      }
    });

    // Get the body content
    const bodyContent = doc.body ? doc.body.innerHTML : doc.documentElement.innerHTML;
    return bodyContent;
  };

  useEffect(() => {
    console.log('Content received in TemplateContent:', content);
    
    if (!containerRef.current) return;

    const container = containerRef.current;
    
    // Apply base styles to container
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.lineHeight = '1.6';
    container.style.padding = displayAsForm ? '2cm' : '0';
    container.style.margin = '0 auto';
    container.style.maxWidth = '100%';
    container.style.boxSizing = 'border-box';
    container.style.color = '#000';
    
    // Style all tables
    container.querySelectorAll('table').forEach(table => {
      table.style.width = '90%';
      table.style.borderCollapse = 'collapse';
      table.style.margin = '1.6em auto';
      table.style.tableLayout = 'fixed';
      table.style.border = '1px solid #444';
    });

    // Style all table cells
    container.querySelectorAll('td, th').forEach(cell => {
      const element = cell as HTMLTableCellElement;
      element.style.border = '1px solid #444';
      element.style.padding = '0.8em';
      element.style.textAlign = 'left';
      element.style.verticalAlign = 'top';
      element.style.lineHeight = '1.6';
    });

    // First handle table cells with placeholders
    container.querySelectorAll('td .placeholder, th .placeholder').forEach(placeholder => {
      const cell = placeholder.closest('td, th') as HTMLTableCellElement;
      if (cell) {
        cell.style.padding = '0';
      }
    });

    // Then handle all placeholders
    container.querySelectorAll('.placeholder').forEach(placeholder => {
      const field = placeholder as HTMLSpanElement;
      const placeholderKey = field.getAttribute('data-placeholder');
      const placeholderLabel = field.getAttribute('data-label') || '';
      
      if (!placeholderKey) return;

      // Make field editable if in form mode
      field.contentEditable = displayAsForm ? 'true' : 'false';
      field.style.cursor = displayAsForm ? 'text' : 'default';
      field.style.width = '100%';
      field.style.display = 'block';
      field.style.minWidth = '150px';
      field.style.padding = '4px 8px';
      field.style.backgroundColor = displayAsForm ? '#FFFFE0' : 'transparent';
      field.style.border = 'none';
      field.style.lineHeight = '1.6';
      
      // Set initial value and add placeholder behavior
      if (placeholderValues[placeholderKey]) {
        field.textContent = placeholderValues[placeholderKey];
        field.classList.remove('placeholder-hint');
        field.setAttribute('data-filled', 'true');
        field.style.color = '#000';
        field.style.fontStyle = 'normal';
      } else {
        field.textContent = placeholderLabel;
        field.classList.add('placeholder-hint');
        field.setAttribute('data-filled', 'false');
        field.style.color = '#666';
        field.style.fontStyle = 'italic';
      }

      if (displayAsForm) {
        // Handle focus event
        field.addEventListener('focus', () => {
          if (field.classList.contains('placeholder-hint')) {
            field.textContent = '';
            field.classList.remove('placeholder-hint');
            field.style.color = '#000';
            field.style.fontStyle = 'normal';
          }
        });

        // Handle blur event
        field.addEventListener('blur', () => {
          const newValue = field.textContent?.trim() || '';
          if (newValue === '') {
            field.textContent = placeholderLabel;
            field.classList.add('placeholder-hint');
            field.setAttribute('data-filled', 'false');
            field.style.color = '#666';
            field.style.fontStyle = 'italic';
            onPlaceholderChange?.(placeholderKey, '');
          } else {
            field.setAttribute('data-filled', 'true');
            field.style.color = '#000';
            field.style.fontStyle = 'normal';
            onPlaceholderChange?.(placeholderKey, newValue);
          }
        });

        // Prevent line breaks
        field.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        });
      }
    });

  }, [content, onPlaceholderChange, placeholderValues, displayAsForm]);

  const handleSave = () => {
    if (!containerRef.current) return;

    const emptyFields = Array.from(containerRef.current.querySelectorAll('.placeholder'))
      .some(field => {
        const element = field as HTMLSpanElement;
        return !element.textContent?.trim() || element.classList.contains('placeholder-hint');
      });

    if (emptyFields) {
      setShowEmptyFieldsDialog(true);
      setPendingSave(true);
    } else {
      onSave?.();
    }
  };

  const handleConfirmSave = () => {
    setShowEmptyFieldsDialog(false);
    if (pendingSave) {
      onSave?.();
      setPendingSave(false);
    }
  };

  const handleCancelSave = () => {
    setShowEmptyFieldsDialog(false);
    setPendingSave(false);
  };

  // Sanitize the HTML content before rendering
  const sanitizedContent = sanitizeHtml(content);

  return (
    <>
      <div 
        ref={containerRef}
        className={`template-content ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }} 
      />
      <AlertDialog open={showEmptyFieldsDialog} onOpenChange={setShowEmptyFieldsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Empty Fields Detected</AlertDialogTitle>
            <AlertDialogDescription>
              Some fields are empty. Do you still wish to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelSave}>No, continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>Yes, save anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

TemplateContent.displayName = 'TemplateContent';
